import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionTemplate } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { MaskLine } from "./Reveal";
import { IMAGES, PUB } from "@/lib/data";

export default function Hero() {
  const ref = useRef(null);
  // Scroll drives a cream "haze" circle dissolving to reveal the sunset hero.
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const hazeOpacity = useTransform(scrollYProgress, [0.05, 0.45], [1, 0]);
  const hazeScale = useTransform(scrollYProgress, [0.05, 0.5], [1, 1.18]);
  const blurPx = useTransform(scrollYProgress, [0.05, 0.45], [0, 16]);
  const blurFilter = useMotionTemplate`blur(${blurPx}px)`;

  const heroTextOpacity = useTransform(scrollYProgress, [0.4, 0.75], [0, 1]);
  const heroTextY = useTransform(scrollYProgress, [0.4, 0.8], [40, 0]);

  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1]);
  const hint = useTransform(scrollYProgress, [0, 0.1], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative h-[260vh]">
      <div className="sticky top-0 h-[100svh] overflow-hidden border-b-2 border-foreground">

        {/* Sunset harbour hero */}
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img src={IMAGES.hero} alt="Diamond Harbour at sunset" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/10 to-black/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
        </motion.div>

        {/* Revealed hero text (light, for the sunset) */}
        <motion.div style={{ opacity: heroTextOpacity, y: heroTextY }}
          className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pt-32 md:pt-40 pb-20 h-full flex flex-col justify-between">
          <div>
            <p className="overline text-white/85">
              <span className="inline-flex items-center gap-2"><MapPin size={13} /> {PUB.address}</span>
            </p>
            <h1 className="mt-6 font-head font-black tracking-tight leading-[0.9] text-white text-[18vw] sm:text-[15vw] lg:text-[11rem] [text-shadow:0_4px_30px_rgba(0,0,0,0.55)]">
              <MaskLine>Harbour</MaskLine>
              <MaskLine className="text-primary">Haze</MaskLine>
            </h1>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-end">
            <p className="max-w-md text-base md:text-lg text-white/90 [text-shadow:0_2px_16px_rgba(0,0,0,0.6)]">
              A bright, salt-air cafe bar just up the hill from the Diamond Harbour ferry.
              Cold drinks and fresh food, and a van to fetch you from the jetty.
            </p>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a href="#book-table" data-testid="hero-book-table"
                 className="font-head font-bold bg-background text-foreground px-6 py-3.5 rounded-full hover:bg-primary transition-colors">
                Book a Table
              </a>
              <a href="#ferry" data-testid="hero-book-van"
                 className="font-head font-bold bg-primary text-foreground px-6 py-3.5 rounded-full border-2 border-foreground shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all">
                Ferry &amp; Van Lift
              </a>
            </div>
          </div>
        </motion.div>

        {/* Cream haze circle with the logo — dissolves on scroll to reveal the sunset */}
        <div
          data-testid="hero-intro"
          className="absolute inset-0 z-30 flex items-center justify-center px-8 pointer-events-none"
        >
          <motion.img
            src="/hero-haze.png"
            alt="Harbour Haze Cafe Bar"
            style={{ opacity: hazeOpacity, scale: hazeScale, filter: blurFilter }}
            className="w-[92vmin] h-[92vmin] max-w-[720px] max-h-[720px] object-contain"
          />
          <motion.div style={{ opacity: hint }} className="absolute bottom-10 flex flex-col items-center gap-2 text-foreground/60">
            <span className="overline">Scroll to enter</span>
            <ArrowDown className="animate-bounce" size={20} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
